import {
  PublicRequester,
  Requester,
  apiClientWithTenantIdHeader,
} from '@/features/api/client.service.js'
import { AUTH_OSI_CODE } from '@/features/auth/constants/authOsiConstants.js'
import { AUTH_TOKEN_MODES } from '@/features/auth/auth.utils.js'
import { filterUndefined } from '@/features/api/filterUndefined.js'
import { makeApiRequest } from '@/features/api/services/utils/makeApiRequest.js'
import { makeFormData } from '@/features/api/services/utils/makeFormData.js'

export const authApiEndpoints = {
  login: '/Authorization/Authentication/Login',
  loginTenant: '/Authorization/Authentication/LoginTenant',
  forgetMyPassword: '/Authorization/Authentication/ForgetMyPassword',
  socialRegister: '/Authorization/Authentication/SocialRegister',
  socialConnect: '/Authorization/Authentication/SocialConnect',
  myDetails: '/Authorization/Authentication/MyDetails',
  newTenant: '/Authorization/Authentication/NewTenant',
  register: '/Authorization/Authentication/Register',
  registerWebDirect: '/Authorization/Authentication/RegisterWebDirect',
  sendEmailVerification: '/Authorization/Authentication/SendEmailVerification',
  verifyEmailCode: '/Authorization/Authentication/VerifyEmailCode',
  apis: '/Authorization/API',
  roleApi: '/Authorization/RoleAPI',
  roleApiAdd: '/Authorization/RoleAPI/Add',
  roleApiDelete: '/Authorization/RoleAPI/Delete',
  profileOnlineAccess: '/Authorization/Authentication/ProfileOnlineAccess',
  profileSuspendAccess: '/Authorization/Authentication/ProfileSuspendAccess',
  updateMyPassword: '/Authorization/Authentication/UpdateMyPassword',
  osiTenantMailerMigrate: '/Authorization/Authentication/osiTenantMailerMigrate',
  osiTenantMailerMigrateStatus: '/Authorization/Authentication/osiTenantMailerMigrateStatus',
  getOrgMembers: '/Authorization/Authentication/GetOrgMembers',
  makeTenantOrg: '/Authorization/Authentication/MakeTenantOrg',
  addMemberToOrg: '/Authorization/Authentication/AddMemberToOrg',
  removeMemberFromOrg: '/Authorization/Authentication/RemoveMemberToOrg',
  getProfileTenantList: '/Authorization/Authentication/GetProfileTenantList',
  addTenantToProfile: '/Authorization/Authentication/AddTenantToProfile',
  removeTenantFromProfile: '/Authorization/Authentication/RemoveTenantFromProfile',
  osiGetTenantLogo: '/Authorization/Authentication/osiGetTenantLogo',
  osiGetTenantHeader: '/Authorization/Authentication/osiGetTenantHeader',
  logout: '/Authorization/Authentication/Logout',
  getMobileVersion: '/Authorization/Authentication/GetMobileVersion',
  updateMobileVersion: '/Authorization/Authentication/UpdateMobileVersion',
  mobileProfileList: '/Authorization/Authentication/MobileProfileList',
  removeMobileAccount: '/Authorization/Authentication/RemoveMobileAccount',
  connectMobileProfile: '/Authorization/Authentication/ConnectMobileProfile',
  removeConnectedTenant: '/Authorization/Authentication/RemoveConnectedTenant',
  removeTenantProfileConnected: '/Authorization/Authentication/RemoveTenantProfileConnected',
  sendInvitation: '/Authorization/Authentication/SendInvitation',
  sendInvitationCodeMyself: '/Authorization/Authentication/SendInvitationCodeMyself',
}

export const RegisterType = {
  MemberTrial: 0,
  TenantUser: 1,
}

export const SocialLoginType = {
  None: 0,
  Google: 1,
  Facebook: 2,
}

export const AuthApi = {
  loginWithEmail: async props =>
    makeApiRequest(async () => {
      const { email, password, code } = props

      const body = filterUndefined({
        UserName: email,
        Password: password,
        Code: code,
        SocialToken: undefined,
        SocialLogin: SocialLoginType.None,
      })

      return PublicRequester.post({
        url: authApiEndpoints.login,
        data: makeFormData(body),
      })
    })(),

  forgetMyPassword: async props =>
    makeApiRequest(async () => {
      const { email } = props

      const body = filterUndefined({
        Email: email,
      })

      return PublicRequester.post({
        url: authApiEndpoints.forgetMyPassword,
        data: makeFormData(body),
      })
    })(),

  socialLogin: async props =>
    makeApiRequest(async () => {
      const { socialLoginType, socialToken, registerType } = props

      const body = filterUndefined({
        Token: undefined,
        SocialLogin: socialLoginType,
        SocialToken: socialToken,
        RegisterType: registerType,
        IsoLanguageCode: undefined,
      })

      return PublicRequester.post({
        url: authApiEndpoints.login,
        data: makeFormData(body),
      })
    })(),

  socialRegister: async props =>
    makeApiRequest(async () => {
      const { socialLoginType, token, tenantId, registerType } = props

      const body = filterUndefined({
        Tenant_Id: tenantId,
        Token: token,
        SocialLogin: socialLoginType,
        RegisterType: registerType,
        IsoLanguageCode: undefined,
      })

      return PublicRequester.post({
        url: authApiEndpoints.socialRegister,
        data: makeFormData(body),
      })
    })(),

  socialConnect: async props =>
    makeApiRequest(async () => {
      const { socialLoginType, token } = props

      const body = filterUndefined({
        Token: token,
        SocialLogin: socialLoginType,
      })

      return PublicRequester.post({
        url: authApiEndpoints.socialConnect,
        data: makeFormData(body),
      })
    })(),

  // MyDetails must always run with the main login token, never the
  // temporary tenant token (it describes the primary profile).
  myDetails: async () =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.myDetails,
        authTokenMode: AUTH_TOKEN_MODES.main,
      }),
    )(),

  // Issues a temporary tenant-scoped token for the tenant's mobileProfileId.
  // Must be sent with the main login token.
  loginTenant: async profileId =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.loginTenant,
        data: makeFormData({ ProfileId: profileId }),
        authTokenMode: AUTH_TOKEN_MODES.main,
      }),
    )(),

  createNewTenant: async () =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.newTenant,
      }),
    )(),

  register: async data =>
    makeApiRequest(async () => {
      const { email, password, firstName, lastName, tenantId, registerType } = data

      return Requester.post({
        url: authApiEndpoints.register,
        data: makeFormData({
          UserName: email,
          Password: password,
          FirstName: firstName,
          LastName: lastName,
          Tenant_Id: tenantId ?? '',
          RegisterType: registerType,
        }),
      })
    })(),

  sendEmailVerification: async email =>
    makeApiRequest(() =>
      PublicRequester.put({
        url: authApiEndpoints.sendEmailVerification,
        data: makeFormData(
          filterUndefined({
            osiCode: AUTH_OSI_CODE,
            Email: email,
          }),
        ),
      }),
    )(),

  verifyEmailCode: async ({ email, code }) =>
    makeApiRequest(() =>
      PublicRequester.put({
        url: authApiEndpoints.verifyEmailCode,
        data: makeFormData(
          filterUndefined({
            osiCode: AUTH_OSI_CODE,
            Email: email,
            Code: code,
          }),
        ),
      }),
    )(),

  registerWebDirect: async data =>
    makeApiRequest(async () => {
      const {
        email,
        password,
        firstName,
        lastName,
        tenantId,
        registerType,
        dateOfBirth,
        gender,
        isoLanguageCode,
      } = data

      return PublicRequester.post({
        url: authApiEndpoints.registerWebDirect,
        data: makeFormData(
          filterUndefined({
            UserName: email,
            Password: password,
            FirstName: firstName,
            LastName: lastName,
            Tenant_Id: tenantId,
            RegisterType: registerType,
            DateOfBirth: dateOfBirth,
            Gender: gender,
            IsoLanguageCode: isoLanguageCode,
          }),
        ),
      })
    })(),

  apisList: async ({ numberOfObjectsPerPage = 999999 } = {}) =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.apis,
        data: makeFormData({ numberOfObjectsPerPage }),
      }),
    )(),

  // roleId uuid
  roleApiList: async roleId =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.roleApi,
        queryParams: { RoleId: roleId },
      }),
    )(),

  // roleId int
  // apiId int
  roleApiAdd: async ({ roleId, apiId }) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.roleApiAdd,
        data: makeFormData({
          Role_Id: roleId,
          Api_Id: apiId,
        }),
      }),
    )(),

  // id uuid
  roleApiDelete: async id =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.roleApiDelete,
        queryParams: { Id: id },
      }),
    )(),

  // profileId uuid
  profileOnlineAccess: async (profileId, options = {}) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.profileOnlineAccess,
        queryParams: {
          ProfileId: profileId,
          ...(Object.hasOwn(options, 'isSuperAdminLimited') && {
            isSuperAdminLimited: options.isSuperAdminLimited,
          }),
        },
        ...(options.tenantId && {
          headers: { 'tenant-id': options.tenantId },
        }),
        ...(Object.hasOwn(options, 'superAdminLimitedRoleGroupId') && {
          data: makeFormData({
            SuperAdminLimitedRoleGroupId: options.superAdminLimitedRoleGroupId,
          }),
        }),
      }),
    )(),

  // profileId uuid
  profileSuspendAccess: async profileId =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.profileSuspendAccess,
        queryParams: { ProfileId: profileId },
      }),
    )(),

  removeTenantProfileConnected: async (profileId, tenantId) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.removeTenantProfileConnected,
        queryParams: { ProfileId: profileId },
        ...(tenantId ? { apiClient: apiClientWithTenantIdHeader(tenantId) } : {}),
      }),
    )(),

  updateMyPassword: async ({ previousPassword, password }) =>
    makeApiRequest(async () => {
      const body = filterUndefined({
        previousPassword,
        password,
      })

      return Requester.post({
        url: authApiEndpoints.updateMyPassword,
        data: makeFormData(body),
      })
    })(),

  osiTenantMailerMigrate: async ({ tenantId, isMigrated }) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.osiTenantMailerMigrate,
        data: makeFormData({ TenantId: tenantId, isMigrated }),
      }),
    )(),

  osiTenantMailerMigrateStatus: async tenantId =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.osiTenantMailerMigrateStatus,
        data: makeFormData({ TenantId: tenantId }),
      }),
    )(),

  getOrgMembers: async orgTenantId =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.getOrgMembers,
        data: makeFormData({ OrgTenantId: orgTenantId }),
        headers: { 'Tenant-Id': orgTenantId },
        apiClient: apiClientWithTenantIdHeader(orgTenantId),
      }),
    )(),

  makeTenantOrg: async (orgTenantId, isOrg) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.makeTenantOrg,
        queryParams: { TenantId: orgTenantId },
        data: makeFormData({ isOrg }),
      }),
    )(),

  addMemberToOrg: async (orgTenantId, tenantId) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.addMemberToOrg,
        queryParams: { TenantId: tenantId, OrgTenantId: orgTenantId },
        headers: { 'Tenant-Id': orgTenantId },
        apiClient: apiClientWithTenantIdHeader(orgTenantId),
      }),
    )(),

  removeMemberFromOrg: async (orgTenantId, tenantId) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.removeMemberFromOrg,
        queryParams: { TenantId: tenantId, OrgTenantId: orgTenantId },
        headers: { 'Tenant-Id': orgTenantId },
        apiClient: apiClientWithTenantIdHeader(orgTenantId),
      }),
    )(),

  getProfileTenantList: async profileId =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.getProfileTenantList,
        queryParams: { ProfileId: profileId },
      }),
    )(),

  addTenantToProfile: async (profileId, tenantId) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.addTenantToProfile,
        queryParams: { ProfileId: profileId },
        data: makeFormData({ TenantId: tenantId }),
      }),
    )(),

  removeTenantFromProfile: async (profileId, tenantId) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.removeTenantFromProfile,
        queryParams: { ProfileId: profileId },
        data: makeFormData({ TenantId: tenantId }),
      }),
    )(),

  osiGetTenantLogo: async () =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.osiGetTenantLogo,
      }),
    )(),

  osiGetTenantHeader: async () =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.osiGetTenantHeader,
      }),
    )(),

  logout: async () =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.logout,
      }),
    )(),

  // Invalidates a temporary tenant token. The token is passed explicitly so
  // it still works after the temporary token is removed from storage.
  logoutWithToken: async token =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.logout,
        headers: { Authorization: `Bearer ${token}` },
        authTokenMode: AUTH_TOKEN_MODES.none,
      }),
    )(),

  getMobileVersion: async () =>
    makeApiRequest(() =>
      Requester.put({
        url: authApiEndpoints.getMobileVersion,
      }),
    )(),

  updateMobileVersion: async ({ mobileVersion, mobileIOSUrl, mobileAndroidUrl }) =>
    makeApiRequest(() => {
      const body = filterUndefined({
        MobileVersion: mobileVersion,
        MobileIOSURL: mobileIOSUrl,
        MobileAndroidURL: mobileAndroidUrl,
      })

      return Requester.post({
        url: authApiEndpoints.updateMobileVersion,
        data: makeFormData(body),
      })
    })(),

  mobileProfileList: async ({ numberOfObjectsPerPage, pageNumber, search }) =>
    makeApiRequest(() => {
      const body = filterUndefined({
        numberOfObjectsPerPage,
        pageNumber,
        search,
      })

      return Requester.put({
        url: authApiEndpoints.mobileProfileList,
        data: makeFormData(body),
      })
    })(),

  removeMobileAccount: async mobileProfileId =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.removeMobileAccount,
        queryParams: { MobileProfileId: mobileProfileId },
      }),
    )(),

  connectMobileProfile: async props =>
    makeApiRequest(async () => {
      const { tenantId, tenantCode, profileId, email, password, code, invitationCode } = props

      const body = filterUndefined({
        osiCode: AUTH_OSI_CODE,
        TenantId: tenantId,
        TenantCode: tenantCode,
        ProfileId: profileId,
        Email: email,
        Password: password,
        Code: code,
        InvitationCode: invitationCode,
      })

      return Requester.post({
        url: authApiEndpoints.connectMobileProfile,
        data: makeFormData(body),
      })
    })(),

  // Self-service disconnect from a connected tenant (main session token only).
  removeConnectedTenant: async tenantId =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.removeConnectedTenant,
        data: makeFormData({
          osiCode: AUTH_OSI_CODE,
          TenantId: tenantId,
        }),
        authTokenMode: AUTH_TOKEN_MODES.main,
        skipTenantIdHeader: true,
      }),
    )(),

  sendInvitation: async ({ email, tenantId, profileId }) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.sendInvitation,
        data: makeFormData(
          filterUndefined({
            Email: email,
            TenantId: tenantId,
            ProfileId: profileId,
          }),
        ),
        ...(tenantId && {
          headers: { 'tenant-id': tenantId },
        }),
      }),
    )(),

  sendInvitationCodeMyself: async ({ tenantId, previousInvitationCode, email }) =>
    makeApiRequest(() =>
      Requester.post({
        url: authApiEndpoints.sendInvitationCodeMyself,
        data: makeFormData(
          filterUndefined({
            TenantId: tenantId,
            PreviousInvitationCode: previousInvitationCode,
          }),
        ),
        headers: filterUndefined({
          'tenant-id': tenantId,
          Email: email,
        }),
        authTokenMode: AUTH_TOKEN_MODES.main,
        skipTenantIdHeader: true,
      }),
    )(),
}
